import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  User, 
  MessageSquare,
  Building,
  ArrowLeft,
  Globe,
  Shield,
  Award,
  Users,
  Calendar,
  HeadphonesIcon,
  HelpCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { navigateToHelpSupport } from '../../utils/authHelpers';

const Contact = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    phone: '',
    department: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goToHelpCenter = () => {
    navigateToHelpSupport(navigate);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulated form submission
    setTimeout(() => {
      toast.success('Mesajınız başarıyla gönderildi! Uzman ekibimiz en kısa sürede size dönüş yapacak.');
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: '',
        phone: '',
        department: ''
      });
      setIsSubmitting(false);
    }, 2000);
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'E-posta İletişim',
      primary: 'info@beetrax.net',
      secondary: 'www.beetrax.net'
    },
    {
      icon: Phone,
      title: 'Telefon Desteği',
      primary: '+90 (552) 424 11 08',
      secondary: 'Pazartesi - Cuma, 09:00-18:00'
    },
    {
      icon: HeadphonesIcon,
      title: 'Canlı Destek',
      primary: 'Anında Yardım',
      secondary: 'Yardım Bileti desteği'
    },
    {
      icon: MapPin,
      title: 'Ofis Lokasyonu',
      primary: 'Bilişim Vadisi Merkez Ofis',
      secondary: 'Kocaeli, Türkiye'
    }
  ];

  const officeInfo = [
    {
      icon: MapPin,
      title: 'Ana Ofis',
      address: 'Bilişim Vadisi Merkez Ofisi',
      detail: 'Muallimköy, Deniz Cd. No:143-5, 41400 Gebze/Kocaeli'
    },
    {
      icon: Globe,
      title: 'Bölgesel Ofisler',
      address: 'Kocaeli, İstanbul',
      detail: '81 ilde hizmet veriyoruz'
    }
  ];

  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Güvenlik',
      description: 'ISO 27001 sertifikalı güvenlik standartları'
    },
    {
      icon: Award,
      title: 'Deneyim',
      description: '10+ yıllık sektör deneyimi'
    },
    {
      icon: Users,
      title: 'Müşteri Odaklı',
      description: '%99.8 müşteri memnuniyet oranı'
    }
  ];

  const departments = [
    'Genel Bilgi',
    'Satış Departmanı',
    'Teknik Destek',
    'Faturalandırma',
    'İş Ortaklığı',
    'Medya ve Basın'
  ];

  return (
    <div className={`min-h-screen ${
      isDark 
        ? 'bg-gray-900' 
        : 'bg-gray-50'
    }`}>
      
      {/* Navigation Header */}
      <div
        className={`sticky top-0 z-50 ${
          isDark 
            ? 'bg-gray-900 border-gray-800' 
            : 'bg-white border-gray-200'
        } border-b`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:hover:text-gray-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-black dark:text-white" />
              <span className="font-medium text-black dark:text-white text-sm sm:text-base">Ana Sayfa</span>
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button
                onClick={goToHelpCenter}
                className={`text-sm font-medium flex items-center gap-2 ${
                  isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                } transition-colors`}
              >
                <HelpCircle className="w-4 h-4" />
                Yardım Merkezi
              </button>
              <Link
                to="/signin"
                className={`px-4 py-2 ${
                  isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                } text-sm font-medium rounded-lg transition-colors`}
              >
                Giriş Yap
              </Link>
            </div>
            
            {/* Mobile Menu */}
            <div className="flex md:hidden items-center space-x-4">
              <button
                onClick={goToHelpCenter}
                className={`p-2 rounded-lg flex items-center justify-center ${
                  isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                } transition-colors`}
                aria-label="Yardım Merkezi"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              
              <Link
                to="/signin"
                className={`p-2 rounded-lg flex items-center justify-center ${
                  isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                } transition-colors`}
                aria-label="Giriş Yap"
              >
                <User className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Page Title */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            İletişime Geçin
          </h1>
          <p className={`text-lg max-w-3xl mx-auto ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Uzman ekibimizle iletişime geçin ve işletmeniz için en uygun çözümü keşfedin
          </p>
        </div>
      </section>

      {/* Contact Methods Grid */}
      <section className="py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <div
                  key={index}
                  className={`p-6 rounded-lg ${
                    isDark 
                      ? 'bg-gray-800 border border-gray-700' 
                      : 'bg-white border border-gray-200'
                  } transition-all`}
                >
                  <div className="mb-4">
                    <Icon className={`w-6 h-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
                  </div>
                  
                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {method.title}
                  </h3>
                  
                  <p className={`text-base font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-gray-800'
                  }`}>
                    {method.primary}
                  </p>
                  
                  <p className={`text-sm ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {method.secondary}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content - Form & Info */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className={`p-8 rounded-lg ${
                isDark 
                  ? 'bg-gray-800 border border-gray-700' 
                  : 'bg-white border border-gray-200'
              }`}>
                <h2 className={`text-2xl font-bold mb-6 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Bizimle İletişime Geçin
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg ${
                          isDark 
                            ? 'bg-gray-700 border border-gray-600 text-white' 
                            : 'bg-gray-50 border border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-gray-500' : 'focus:ring-gray-400'}`}
                        placeholder="Adınızı ve soyadınızı girin"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg ${
                          isDark 
                            ? 'bg-gray-700 border border-gray-600 text-white' 
                            : 'bg-gray-50 border border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-gray-500' : 'focus:ring-gray-400'}`}
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Şirket
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-lg ${
                          isDark 
                            ? 'bg-gray-700 border border-gray-600 text-white' 
                            : 'bg-gray-50 border border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-gray-500' : 'focus:ring-gray-400'}`}
                        placeholder="Şirket adınız"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Telefon
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-lg ${
                          isDark 
                            ? 'bg-gray-700 border border-gray-600 text-white' 
                            : 'bg-gray-50 border border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-gray-500' : 'focus:ring-gray-400'}`}
                        placeholder="+90 (5xx) xxx xx xx"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Departman
                    </label>
                    <select
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg ${
                        isDark 
                          ? 'bg-gray-700 border border-gray-600 text-white' 
                          : 'bg-gray-50 border border-gray-300 text-gray-900'
                      } focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-gray-500' : 'focus:ring-gray-400'}`}
                    >
                      <option value="">Bir departman seçin</option>
                      {departments.map((dept, index) => (
                        <option key={index} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Konu *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 rounded-lg ${
                        isDark 
                          ? 'bg-gray-700 border border-gray-600 text-white' 
                          : 'bg-gray-50 border border-gray-300 text-gray-900'
                      } focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-gray-500' : 'focus:ring-gray-400'}`}
                      placeholder="Mesajınızın konusu"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Mesaj *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className={`w-full px-4 py-3 rounded-lg resize-none ${
                        isDark 
                          ? 'bg-gray-700 border border-gray-600 text-white' 
                          : 'bg-gray-50 border border-gray-300 text-gray-900'
                      } focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-gray-500' : 'focus:ring-gray-400'}`}
                      placeholder="Mesajınızı detaylıca açıklayın..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-center gap-2 px-6 py-3 ${
                      isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-white'
                        : 'bg-gray-800 hover:bg-gray-700 text-white'
                    } font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Gönderiliyor...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Mesaj Gönder</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Side Information */}
            <div className="space-y-8">
              {/* Office Information */}
              <div className={`p-6 rounded-lg ${
                isDark 
                  ? 'bg-gray-800 border border-gray-700' 
                  : 'bg-white border border-gray-200'
              }`}>
                <h3 className={`text-lg font-bold mb-4 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Ofis Bilgileri
                </h3>
                
                <div className="space-y-6">
                  {officeInfo.map((info, index) => {
                    const Icon = info.icon;
                    return (
                      <div key={index} className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <Icon className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                        </div>
                        <div>
                          <h4 className={`font-medium mb-1 ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                            {info.title}
                          </h4>
                          <p className={`text-sm mb-1 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {info.address}
                          </p>
                          <p className={`text-sm ${
                            isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {info.detail}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Why Choose Us */}
              <div className={`p-6 rounded-lg ${
                isDark 
                  ? 'bg-gray-800 border border-gray-700' 
                  : 'bg-white border border-gray-200'
              }`}>
                <h3 className={`text-lg font-bold mb-4 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Neden Beetrax?
                </h3>
                
                <div className="space-y-4">
                  {whyChooseUs.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          <Icon className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                        </div>
                        <div>
                          <h4 className={`font-medium text-sm ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                            {item.title}
                          </h4>
                          <p className={`text-xs ${
                            isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {item.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className={`text-2xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Ofis Konumumuz
            </h2>
          </div>
          
          <div className={`relative rounded-lg overflow-hidden ${
            isDark 
              ? 'bg-gray-800 border border-gray-700' 
              : 'bg-white border border-gray-200'
          }`}>
            <div className="aspect-video w-full">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3020.6481149364004!2d29.507842176856894!3d40.79174957138166!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cb27fb1db38eab%3A0x7302dcaf2b0a0be1!2sTEIRA3D!5e0!3m2!1str!2str!4v1749556945825!5m2!1str!2str" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
                title="Bilişim Vadisi Konum"
              ></iframe>
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gray-800/80 backdrop-blur-sm p-4 text-center">
              <p className={`text-sm text-white mb-2`}>
                Muallimköy, Deniz Cd. No:143-5, 41400 Gebze/Kocaeli
              </p>
              <a 
                href="https://maps.app.goo.gl/vneJWGTAFSNioAKq9" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`inline-block px-4 py-2 ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                } text-sm font-medium rounded-lg transition-colors text-white`}
              >
                Yol Tarifi Al
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-12 border-t ${
        isDark ? 'border-gray-800' : 'border-gray-200'
      }`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-2xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Hemen Başlayın
          </h2>
          <p className={`text-lg mb-6 max-w-2xl mx-auto ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            İşletmenizi dijitalleştirme yolculuğunda yanınızdayız.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className={`px-6 py-3 ${
              isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-800 hover:bg-gray-700 text-white'
            } font-medium rounded-lg transition-colors`}>
              Ücretsiz Demo
            </button>
            <button className={`px-6 py-3 ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            } font-medium rounded-lg transition-colors`}>
              Fiyatlandırma
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact; 